import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Clock3, Plus, Receipt, Trash2, Wrench, X } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";
import { RecipesTab, CostsTab } from "./RecipesTab";
import { StockTab } from "./StockTab";
import { OeeTab } from "./OeeTab";
import { BatchesTab } from "./BatchesTab";
import { CostAnalysisTab } from "./CostAnalysisTab";

const STATUSES = [
    { id: "nieuw", label: "Nieuw" },
    { id: "in_productie", label: "In productie" },
    { id: "wacht_materiaal", label: "Wacht op materiaal" },
    { id: "gereed", label: "Gereed" },
];

const TABS = [
    { id: "orders", label: "Orders" },
    { id: "recepturen", label: "Recepturen & kostprijs" },
    { id: "kostenanalyse", label: "Kostenanalyse" },
    { id: "kosten", label: "Kosten & tarieven" },
    { id: "voorraad", label: "Voorraad & inkoop" },
    { id: "batches", label: "Batches (HACCP)" },
    { id: "oee", label: "OEE" },
];

const EMPTY_FORM = { customer: "", description: "", quantity: 1, due_date: "", notes: "", machine: "" };

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

export const ProductionModule = () => {
    const [orders, setOrders] = useState([]);
    const [machines, setMachines] = useState([]);
    const [machineName, setMachineName] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [stepText, setStepText] = useState("");
    const [timeForm, setTimeForm] = useState({ hours: "", note: "", date: "" });
    const [tab, setTab] = useState("orders");
    const [invoiceNotice, setInvoiceNotice] = useState("");
    const [regForm, setRegForm] = useState({});

    const saveRegistration = async (o) => {
        const f = regForm[o.id] || {};
        const update = {};
        if (f.produced_quantity !== undefined && f.produced_quantity !== "") update.produced_quantity = Number(f.produced_quantity);
        if (f.waste_perc !== undefined && f.waste_perc !== "") update.waste_perc = Number(f.waste_perc);
        if (!Object.keys(update).length) return;
        await patch(o, update);
    };

    const invoiceFromOrder = async (o) => {
        setInvoiceNotice("");
        setError("");
        try {
            const { data } = await platformClient.post(`/production/orders/${o.id}/invoice`);
            setInvoiceNotice(`${data.number} aangemaakt (€ ${Number(data.total).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}) — te vinden bij Facturatie`);
            setTimeout(() => setInvoiceNotice(""), 6000);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const load = useCallback(async () => {
        try {
            const [o, m] = await Promise.all([
                platformClient.get("/production/orders"),
                platformClient.get("/production/machines"),
            ]);
            setOrders(o.data);
            setMachines(m.data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/production/orders", {
                ...form,
                quantity: Number(form.quantity) || 1,
            });
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const patch = async (order, update) => {
        setOrders((list) => list.map((o) => (o.id === order.id ? { ...o, ...update } : o)));
        try {
            await platformClient.patch(`/production/orders/${order.id}`, update);
        } catch {
            load();
        }
    };

    const setStatus = (order, status) => patch(order, { status });

    const remove = async (order) => {
        if (!window.confirm(`Order ${order.order_number} definitief verwijderen?`)) return;
        setOrders((list) => list.filter((o) => o.id !== order.id));
        try {
            await platformClient.delete(`/production/orders/${order.id}`);
        } catch {
            load();
        }
    };

    const addMachine = async (e) => {
        e.preventDefault();
        if (!machineName.trim()) return;
        try {
            await platformClient.post("/production/machines", { name: machineName.trim() });
            setMachineName("");
            load();
        } catch {}
    };

    const removeMachine = async (m) => {
        if (!window.confirm(`Machine ${m.name} verwijderen?`)) return;
        try {
            await platformClient.delete(`/production/machines/${m.id}`);
            load();
        } catch {}
    };

    const steps = (o) => o.steps || [];
    const entries = (o) => o.time_entries || [];
    const totalHours = (o) => entries(o).reduce((sum, t) => sum + (t.hours || 0), 0);

    const toggleStep = (o, idx) =>
        patch(o, { steps: steps(o).map((s, i) => (i === idx ? { ...s, done: !s.done } : s)) });

    const addStep = (o) => {
        if (!stepText.trim()) return;
        patch(o, { steps: [...steps(o), { label: stepText.trim(), done: false }] });
        setStepText("");
    };

    const delStep = (o, idx) => patch(o, { steps: steps(o).filter((_, i) => i !== idx) });

    const addTime = (o) => {
        const hours = Number(timeForm.hours);
        if (!hours || hours <= 0) return;
        patch(o, { time_entries: [...entries(o), { hours, note: timeForm.note, date: timeForm.date }] });
        setTimeForm({ hours: "", note: "", date: "" });
    };

    const counts = Object.fromEntries(
        STATUSES.map((s) => [s.id, orders.filter((o) => o.status === s.id).length])
    );

    return (
        <div data-testid="production-module" className="mt-8">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {STATUSES.map((s) => (
                    <div
                        key={s.id}
                        data-testid={`production-stat-${s.id}`}
                        className="border border-white/10 bg-coal p-5"
                    >
                        <p className="font-display text-3xl text-gold">{counts[s.id]}</p>
                        <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ash">
                            {s.label}
                        </p>
                    </div>
                ))}
            </div>

            <div data-testid="production-tabs" className="mt-8 flex flex-wrap gap-2">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`production-tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`border px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                            tab === t.id
                                ? "border-gold bg-gold/10 text-gold"
                                : "border-white/10 text-ash hover:border-gold/40 hover:text-bone"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {invoiceNotice ? (
                <p data-testid="production-invoice-notice" className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold">
                    {invoiceNotice}
                </p>
            ) : null}
            {error && !showForm ? (
                <p data-testid="production-error" className="mt-4 border border-red-400/30 bg-red-400/5 px-4 py-3 text-xs text-red-300">
                    {error}
                </p>
            ) : null}

            {tab === "recepturen" ? (
                <RecipesTab machines={machines} />
            ) : tab === "kosten" ? (
                <CostsTab />
            ) : tab === "voorraad" ? (
                <StockTab />
            ) : tab === "kostenanalyse" ? (
                <CostAnalysisTab />
            ) : tab === "batches" ? (
                <BatchesTab />
            ) : tab === "oee" ? (
                <OeeTab />
            ) : (
                <>
            <div className="mt-6 flex flex-wrap items-center gap-3">
                <Wrench className="h-4 w-4 text-gold" />
                {machines.map((m) => {
                    const open = orders.filter((o) => o.machine === m.name && o.status !== "gereed").length;
                    return (
                        <span
                            key={m.id}
                            data-testid={`machine-chip-${m.id}`}
                            className="flex items-center gap-2 border border-white/10 bg-coal px-3 py-1.5 text-xs text-bone"
                        >
                            {m.name}
                            <span className={open ? "text-gold" : "text-ash/50"}>
                                {open} open
                            </span>
                            <button
                                data-testid={`machine-delete-${m.id}`}
                                onClick={() => removeMachine(m)}
                                aria-label="Machine verwijderen"
                                className="text-ash/50 transition-colors hover:text-red-400"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    );
                })}
                <form onSubmit={addMachine} className="flex items-center gap-2">
                    <input
                        data-testid="machine-add-input"
                        value={machineName}
                        onChange={(e) => setMachineName(e.target.value)}
                        placeholder="Machine toevoegen…"
                        maxLength={80}
                        className="w-40 border-b border-white/20 bg-transparent pb-1.5 text-xs text-bone placeholder:text-ash/50 focus:border-gold focus:outline-none"
                    />
                    <button
                        data-testid="machine-add-button"
                        type="submit"
                        aria-label="Machine toevoegen"
                        className="border border-gold/50 p-1.5 text-gold transition-colors hover:bg-gold hover:text-black"
                    >
                        <Plus className="h-3.5 w-3.5" />
                    </button>
                </form>
            </div>

            <div className="mt-8 flex items-center justify-between">
                <h3 className="font-display text-2xl tracking-tight">Orders ({orders.length})</h3>
                <button
                    data-testid="production-add-toggle"
                    onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-2 bg-gold px-5 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                >
                    {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {showForm ? "Sluiten" : "Nieuwe order"}
                </button>
            </div>

            {showForm ? (
                <form
                    data-testid="production-create-form"
                    onSubmit={create}
                    className="mt-6 border border-white/10 bg-coal p-6 md:p-8"
                >
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <label className="block">
                            <span className={labelCls}>Klantnaam</span>
                            <input
                                data-testid="production-form-customer"
                                value={form.customer}
                                onChange={(e) => setForm({ ...form, customer: e.target.value })}
                                required
                                maxLength={120}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Gewenste datum</span>
                            <input
                                data-testid="production-form-due-date"
                                type="date"
                                value={form.due_date}
                                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                                className={`${inputCls} [color-scheme:dark]`}
                            />
                        </label>
                    </div>
                    <label className="mt-6 block">
                        <span className={labelCls}>Omschrijving</span>
                        <input
                            data-testid="production-form-description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            required
                            maxLength={500}
                            className={inputCls}
                        />
                    </label>
                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
                        <label className="block">
                            <span className={labelCls}>Aantal</span>
                            <input
                                data-testid="production-form-quantity"
                                type="number"
                                min={1}
                                value={form.quantity}
                                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Machine</span>
                            <select
                                data-testid="production-form-machine"
                                value={form.machine}
                                onChange={(e) => setForm({ ...form, machine: e.target.value })}
                                className="mt-2 w-full border-b border-white/20 bg-ink pb-2.5 text-sm text-bone focus:border-gold focus:outline-none"
                            >
                                <option value="">— geen —</option>
                                {machines.map((m) => (
                                    <option key={m.id} value={m.name}>
                                        {m.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className={labelCls}>Notitie (optioneel)</span>
                            <input
                                data-testid="production-form-notes"
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                maxLength={2000}
                                className={inputCls}
                            />
                        </label>
                    </div>
                    {error ? (
                        <p data-testid="production-form-error" className="mt-4 text-sm text-red-400">
                            {error}
                        </p>
                    ) : null}
                    <button
                        data-testid="production-form-submit"
                        type="submit"
                        disabled={busy}
                        className="mt-8 bg-gold px-8 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                    >
                        {busy ? "Opslaan…" : "Order aanmaken"}
                    </button>
                </form>
            ) : null}

            {orders.length === 0 ? (
                <p data-testid="production-empty" className="mt-8 text-sm text-ash">
                    Nog geen orders. Maak je eerste productieorder aan.
                </p>
            ) : (
                <div className="mt-6 space-y-3">
                    {orders.map((o) => {
                        const doneCount = steps(o).filter((s) => s.done).length;
                        return (
                            <div
                                key={o.id}
                                data-testid={`production-order-${o.id}`}
                                className="border border-white/10 bg-coal p-5"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <p className="text-sm text-bone">
                                            <span className="text-gold">{o.order_number}</span>
                                            {" — "}
                                            {o.customer}
                                        </p>
                                        <p className="mt-1 text-xs text-ash">
                                            {o.description} · {o.quantity} stuks
                                            {o.due_date ? ` · ${o.due_date}` : ""}
                                            {o.machine ? ` · ${o.machine}` : ""}
                                            {steps(o).length ? ` · ${doneCount}/${steps(o).length} stappen` : ""}
                                            {totalHours(o) ? ` · ${totalHours(o)}u` : ""}
                                        </p>
                                        {o.notes ? (
                                            <p className="mt-1 text-xs italic text-ash/70">{o.notes}</p>
                                        ) : null}
                                        {o.used_batches?.length ? (
                                            <p data-testid={`order-batches-${o.id}`} className="mt-1 text-[11px] text-ash/80">
                                                Batches: {o.used_batches.map((b) => `${b.batch_number} (${b.quantity})`).join(" · ")}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            data-testid={`production-expand-${o.id}`}
                                            onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                                            aria-label="Details"
                                            className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                        >
                                            {expanded === o.id ? (
                                                <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ChevronRight className="h-4 w-4" />
                                            )}
                                        </button>
                                        <select
                                            data-testid={`production-status-${o.id}`}
                                            value={o.status}
                                            onChange={(e) => setStatus(o, e.target.value)}
                                            className="border border-white/15 bg-ink px-3 py-2 text-xs uppercase tracking-[0.15em] text-bone transition-colors focus:border-gold focus:outline-none"
                                        >
                                            {STATUSES.map((s) => (
                                                <option key={s.id} value={s.id}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            data-testid={`production-delete-${o.id}`}
                                            onClick={() => remove(o)}
                                            aria-label="Verwijderen"
                                            className="border border-white/15 p-2.5 text-ash transition-colors hover:border-red-400/60 hover:text-red-400"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                {expanded === o.id ? (
                                    <div className="mt-5 grid grid-cols-1 gap-8 border-t border-white/10 pt-5 md:grid-cols-3">
                                        <div>
                                            <p className={labelCls}>
                                                Productiestappen ({doneCount}/{steps(o).length})
                                            </p>
                                            <div className="mt-4 space-y-2">
                                                {steps(o).map((s, i) => (
                                                    <div key={i} className="flex items-center gap-3">
                                                        <button
                                                            data-testid={`step-toggle-${o.id}-${i}`}
                                                            onClick={() => toggleStep(o, i)}
                                                            className={`flex h-4 w-4 items-center justify-center border transition-colors ${
                                                                s.done
                                                                    ? "border-gold bg-gold text-black"
                                                                    : "border-white/25 text-transparent hover:border-gold/60"
                                                            }`}
                                                        >
                                                            <svg viewBox="0 0 10 8" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M1 4l2.5 2.5L9 1" />
                                                            </svg>
                                                        </button>
                                                        <span
                                                            className={`text-sm ${s.done ? "text-ash line-through" : "text-bone"}`}
                                                        >
                                                            {s.label}
                                                        </span>
                                                        <button
                                                            data-testid={`step-delete-${o.id}-${i}`}
                                                            onClick={() => delStep(o, i)}
                                                            aria-label="Stap verwijderen"
                                                            className="ml-auto text-ash/40 transition-colors hover:text-red-400"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mt-3 flex gap-2">
                                                <input
                                                    data-testid={`step-add-input-${o.id}`}
                                                    value={stepText}
                                                    onChange={(e) => setStepText(e.target.value)}
                                                    placeholder="Stap toevoegen (bijv. Lassen)…"
                                                    maxLength={120}
                                                    className="w-full border-b border-white/20 bg-transparent pb-1.5 text-xs text-bone placeholder:text-ash/50 focus:border-gold focus:outline-none"
                                                />
                                                <button
                                                    data-testid={`step-add-button-${o.id}`}
                                                    onClick={() => addStep(o)}
                                                    aria-label="Stap toevoegen"
                                                    className="border border-gold/50 p-1.5 text-gold transition-colors hover:bg-gold hover:text-black"
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                            <div className="mt-5 border-t border-white/10 pt-4">
                                                <p className={labelCls}>Registratie</p>
                                                <div className="mt-2 flex flex-wrap items-end gap-2">
                                                    <label className="block">
                                                        <span className="text-[10px] text-ash/70">Geproduceerd</span>
                                                        <input
                                                            data-testid={`order-produced-${o.id}`}
                                                            type="number" min="0" step="any"
                                                            className="mt-1 w-28 border-b border-white/20 bg-transparent pb-1.5 text-sm text-bone focus:border-gold focus:outline-none"
                                                            value={regForm[o.id]?.produced_quantity ?? o.produced_quantity ?? ""}
                                                            onChange={(e) => setRegForm((s) => ({ ...s, [o.id]: { ...(s[o.id] || {}), produced_quantity: e.target.value } }))}
                                                        />
                                                    </label>
                                                    <label className="block">
                                                        <span className="text-[10px] text-ash/70">Uitval %</span>
                                                        <input
                                                            data-testid={`order-waste-${o.id}`}
                                                            type="number" min="0" max="100" step="any"
                                                            className="mt-1 w-20 border-b border-white/20 bg-transparent pb-1.5 text-sm text-bone focus:border-gold focus:outline-none"
                                                            value={regForm[o.id]?.waste_perc ?? o.waste_perc ?? ""}
                                                            onChange={(e) => setRegForm((s) => ({ ...s, [o.id]: { ...(s[o.id] || {}), waste_perc: e.target.value } }))}
                                                        />
                                                    </label>
                                                    <button
                                                        data-testid={`order-reg-save-${o.id}`}
                                                        onClick={() => saveRegistration(o)}
                                                        className="border border-gold/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                                    >
                                                        Opslaan
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <p className={labelCls}>Machine</p>
                                            <select
                                                data-testid={`order-machine-${o.id}`}
                                                value={o.machine || ""}
                                                onChange={(e) => patch(o, { machine: e.target.value })}
                                                className="mt-4 w-full border border-white/15 bg-ink px-3 py-2 text-sm text-bone focus:border-gold focus:outline-none"
                                            >
                                                <option value="">— geen —</option>
                                                {machines.map((m) => (
                                                    <option key={m.id} value={m.name}>
                                                        {m.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <p className={labelCls}>
                                                <Clock3 className="mr-1 inline h-3 w-3" /> Tijd (
                                                {totalHours(o)}u totaal)
                                            </p>
                                            <div className="mt-4 space-y-1.5">
                                                {entries(o).map((t, i) => (
                                                    <p key={i} className="text-xs text-ash">
                                                        <span className="text-gold">{t.hours}u</span>
                                                        {t.date ? ` · ${t.date}` : ""}
                                                        {t.note ? ` · ${t.note}` : ""}
                                                    </p>
                                                ))}
                                            </div>
                                            <div className="mt-3 grid grid-cols-3 gap-2">
                                                <input
                                                    data-testid={`time-hours-${o.id}`}
                                                    type="number"
                                                    min="0.25"
                                                    step="0.25"
                                                    value={timeForm.hours}
                                                    onChange={(e) =>
                                                        setTimeForm({ ...timeForm, hours: e.target.value })
                                                    }
                                                    placeholder="uren"
                                                    className="border-b border-white/20 bg-transparent pb-1.5 text-xs text-bone placeholder:text-ash/50 focus:border-gold focus:outline-none"
                                                />
                                                <input
                                                    data-testid={`time-date-${o.id}`}
                                                    type="date"
                                                    value={timeForm.date}
                                                    onChange={(e) =>
                                                        setTimeForm({ ...timeForm, date: e.target.value })
                                                    }
                                                    className="border-b border-white/20 bg-transparent pb-1.5 text-xs text-bone [color-scheme:dark] focus:border-gold focus:outline-none"
                                                />
                                                <input
                                                    data-testid={`time-note-${o.id}`}
                                                    value={timeForm.note}
                                                    onChange={(e) =>
                                                        setTimeForm({ ...timeForm, note: e.target.value })
                                                    }
                                                    placeholder="notitie"
                                                    maxLength={200}
                                                    className="border-b border-white/20 bg-transparent pb-1.5 text-xs text-bone placeholder:text-ash/50 focus:border-gold focus:outline-none"
                                                />
                                            </div>
                                            <button
                                                data-testid={`time-add-${o.id}`}
                                                onClick={() => addTime(o)}
                                                className="mt-3 border border-gold/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                            >
                                                Uren toevoegen
                                            </button>
                                            {totalHours(o) > 0 ? (
                                                <button
                                                    data-testid={`order-invoice-${o.id}`}
                                                    onClick={() => invoiceFromOrder(o)}
                                                    className="mt-3 flex items-center gap-2 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/50 hover:text-gold"
                                                >
                                                    <Receipt className="h-3 w-3" /> Factuur van uren
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
                </>
            )}
        </div>
    );
};
